import * as React from 'react';

import { formsApi, FieldDTO, OptionDTO } from '../api/formsApi';

type OptionsCache = Record<string, OptionDTO[]>;

export function useFormFields() {
  const [fields, setFields] = React.useState<FieldDTO[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [optionsCache, setOptionsCache] = React.useState<OptionsCache>({});

  React.useEffect(() => {
    let live = true;
    setLoading(true);
    formsApi
      .getAllFields()
      .then(list => {
        if (!live) return;
        setFields(list);
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, []);

  const getOptions = React.useCallback(
    async (fieldId: string): Promise<OptionDTO[]> => {
      if (!fieldId) return [];
      if (optionsCache[fieldId]) return optionsCache[fieldId];
      const list = await formsApi.getFieldOptions(fieldId);
      setOptionsCache(prev => ({ ...prev, [fieldId]: list }));
      return list;
    },
    [optionsCache]
  );

  // Cache para evitar buscas repetidas
  const [optionToFieldCache, setOptionToFieldCache] = React.useState<Record<string, string>>({});

  // Função para encontrar o campo que contém uma opção específica
  const findFieldByOptionId = React.useCallback(
    async (optionId: string): Promise<FieldDTO | null> => {
      if (!optionId) return null;

      // Verificar cache primeiro
      if (optionToFieldCache[optionId]) {
        const cachedFieldId = optionToFieldCache[optionId];
        const field = fields.find(f => f.id === cachedFieldId);
        if (field) {
          console.log(`🎯 Campo encontrado no cache para opção ${optionId}:`, field);
          return field;
        }
      }

      // Buscar em todos os campos
      for (const field of fields) {
        try {
          const options = await getOptions(field.id);
          const option = options.find(opt => opt.id === optionId);
          if (option) {
            // Adicionar ao cache
            setOptionToFieldCache(prev => ({
              ...prev,
              [optionId]: field.id,
            }));

            return field;
          }
        } catch (error) {
          console.error(`Erro ao buscar opções do campo ${field.id}:`, error);
        }
      }

      console.log(`❌ Campo não encontrado para opção ${optionId}`);
      return null;
    },
    [fields, getOptions]
  );

  return { fields, loading, getOptions, optionsCache, findFieldByOptionId };
}
